import { HTTP_STATUS } from 'cpool-constants';

import { API } from '~/config.ts';

interface Context extends RequestInit {
    body: XMLHttpRequestBodyInit;
}

type UploadOptions = {
    /** Reports the upload progress as a whole-number percentage (0–100). */
    onProgress?: (percent: number) => void;
    /** Aborts the request when the signal fires; the promise then resolves with `status: 0`. */
    signal?: AbortSignal;
    /** Aborts when no upload progress arrives for this many ms; the promise then resolves with `status: 408`. */
    idleTimeout?: number;
};

const uploadFiles = async (route: string, context: Context, options?: UploadOptions): Promise<Response> => {
    const xhr = new XMLHttpRequest();
    return new Promise((resolve) => {
        // abort() before open() is a no-op, so the request would still be sent
        if (options?.signal?.aborted) {
            resolve({ ...new Response(), body: null, headers: new Headers(), ok: false, status: 0, statusText: '' });
            return;
        }

        // Idle abort fires loadend with status 0; flag lets loadend report 408
        let timedOut = false;
        let idleTimer: ReturnType<typeof setTimeout> | undefined;
        const idleTimeout = options?.idleTimeout;
        const armIdleTimer = () => {
            if (!idleTimeout) return;
            clearTimeout(idleTimer);
            idleTimer = setTimeout(() => {
                timedOut = true;
                xhr.abort();
            }, idleTimeout);
        };

        if (options?.onProgress || idleTimeout) {
            xhr.upload.addEventListener('progress', (event) => {
                armIdleTimer();
                if (event.lengthComputable) options?.onProgress?.(Math.round((event.loaded / event.total) * 100));
            });
            // Body fully sent; waiting for the server response has no timeout
            xhr.upload.addEventListener('load', () => clearTimeout(idleTimer));
        }

        // Abort fires loadend with status 0; caller checks signal.aborted
        options?.signal?.addEventListener('abort', () => xhr.abort(), { once: true });

        xhr.addEventListener('loadend', () => {
            clearTimeout(idleTimer);
            const { response, statusText } = xhr;
            const status = timedOut ? HTTP_STATUS.REQUEST_TIMEOUT : xhr.status;
            let jsonData = null;
            // Blackrik sends errors as plain text; wrap so callers can read body.message
            if (typeof response === 'string' && response.length > 0) {
                try {
                    jsonData = JSON.parse(response);
                } catch {
                    if (status < 200 || status > 299) {
                        jsonData = { message: response };
                    }
                }
            }
            const headers: { [key: string]: string } = {};
            xhr.getAllResponseHeaders()
                .trim()
                .split(/[\r\n]+/)
                .forEach((line) => {
                    const parts = line.split(': ');
                    const header = parts.shift();
                    const value = parts.join(': ');
                    if (header) headers[header] = value;
                });

            resolve({
                ...new Response(),
                body: jsonData,
                headers: new Headers(headers),
                ok: status >= 200 && status <= 299,
                status,
                statusText,
            });
        });
        xhr.open(context.method ?? 'get', `${API.URL}${route}`, true);
        xhr.withCredentials = true;
        if (context.headers)
            Object.entries(context.headers).forEach(([header, value]) => xhr.setRequestHeader(header, value));
        xhr.send(context.body);
        armIdleTimer();
    });
};

export default uploadFiles;
